// File: src/components/questionnaires/ResultsScreen.js
// -----------------------------------------------------------------------------
// Écran des résultats du questionnaire (générique)
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ParentShareHybrid from './ParentShareHybrid';
import { AuthContext } from '../../context/AuthContext';
import { HistoryContext } from '../../context/HistoryContext';
import { vfUpdateVariables } from '../../api/VoiceflowInteractions';

function ItemModal({ item, onClose }) {
  // Détecter si c'est une modale enrichie (avec strengths, signesDetecteurs, etc.)
  const isEnriched = item.strengths?.length > 0 || item.signesDetecteurs?.length > 0;

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className={`modal-content ${isEnriched ? 'im-modal-content' : ''}`}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>

        <div className="modal-emoji">{item.emoji}</div>
        <h3 className="modal-title">{item.name}</h3>
        <p className="modal-subtitle">{item.subtitle}</p>

        <div className="modal-divider"></div>

        <p className="modal-description">{item.description}</p>

        {/* Section enrichie : forces */}
        {item.strengths?.length > 0 && (
          <div className="modal-strengths">
            {item.strengths.map((s, i) => (
              <span key={i} className="strength-tag" style={{ borderColor: item.color, color: item.color }}>
                {s}
              </span>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function ResultsScreen({ results: initialResults, config, onRestart }) {
  const { user } = useContext(AuthContext);
  const { history } = useContext(HistoryContext);
  const {
    pageTitle,
    topSectionTitle,
    allSectionTitle,
    allSectionSubtitle,
    synthesisSectionTitle,
    attribution,
    disclaimer,
    topCount,
    processResults,
    generateSynthesis,
    synthesisKey,
    luxVariableName,
    getHollandCode,
    sendAgreement
  } = config;

  const [selectedItemModal, setSelectedItemModal] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [isLoadingSynthesis, setIsLoadingSynthesis] = useState(true);
  const [synthesisError, setSynthesisError] = useState(false);

  // Transformer les résultats en tableau trié
  const processedItems = React.useMemo(() => {
    return processResults(initialResults);
  }, [initialResults, processResults]);

  const topItems = processedItems.slice(0, topCount);
  const hollandCode = getHollandCode ? getHollandCode(initialResults) : null;

  // Compter le nombre de questionnaires complétés
  const countCompletedSurveys = () => {
    if (!history) return 0;
    const surveyTypes = [
      'VALUES_SURVEY_COMPLETED',
      'RIASEC_SURVEY_COMPLETED',
      'JOURDA_SURVEY_COMPLETED',
      'IM_SURVEY_COMPLETED'
    ];
    return history.filter(item => surveyTypes.includes(item.type)).length;
  };

  // Fonction pour envoyer la synthèse à LUX
  const sendSynthesisToLux = async (synthesisText) => {
    if (!luxVariableName || !user?.id || !synthesisText) return;

    try {
      const luxSessionId = `lux_${user.id}`;
      const questionnairesCompleted = countCompletedSurveys();
      await vfUpdateVariables(
        luxSessionId,
        {
          [luxVariableName]: synthesisText,
          questionnaires_completed: questionnairesCompleted
        },
        process.env.REACT_APP_VF_LUX_API_KEY
      );
      console.log(`Variable LUX ${luxVariableName} mise à jour, questionnaires_completed: ${questionnairesCompleted}`);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la synthèse à LUX:', error);
    }
  };

  // Charger la synthèse quand les résultats arrivent
  useEffect(() => {
    if (!initialResults) {
      return;
    }

    // Si la synthèse est déjà dans les résultats, l'utiliser
    if (initialResults[synthesisKey]) {
      setSynthesis(initialResults[synthesisKey]);
      sendSynthesisToLux(initialResults[synthesisKey]);
      setIsLoadingSynthesis(false);
      return;
    }

    // Sinon, charger la synthèse via l'API
    async function loadSynthesis() {
      setIsLoadingSynthesis(true);
      setSynthesisError(false);

      try {
        const result = await generateSynthesis();
        const synthesisText = result[synthesisKey] || result.synthesis;
        setSynthesis(synthesisText);
        sendSynthesisToLux(synthesisText);
      } catch (error) {
        console.error('Erreur lors de la génération de la synthèse:', error);
        setSynthesisError(true);
      } finally {
        setIsLoadingSynthesis(false);
      }
    }

    loadSynthesis();
  }, [initialResults, generateSynthesis, synthesisKey]);

  const handleRetrySynthesis = async () => {
    setIsLoadingSynthesis(true);
    setSynthesisError(false);

    try {
      const result = await generateSynthesis();
      setSynthesis(result[synthesisKey] || result.synthesis);
    } catch (error) {
      console.error('Erreur lors de la génération de la synthèse:', error);
      setSynthesisError(true);
    } finally {
      setIsLoadingSynthesis(false);
    }
  };

  if (!initialResults) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 flex flex-col items-center bg-gray-900 min-h-screen overflow-y-auto">

      {/* Blobs decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-72 h-72 rounded-full blur-[80px] opacity-30 bg-indigo-500 -top-24 -right-24 animate-blob1" />
        <div className="absolute w-52 h-52 rounded-full blur-[80px] opacity-25 bg-pink-500 bottom-1/5 -right-16 animate-blob3" />
      </div>
      <div className="w-full md:max-w-[50%] p-5">
      {/* Header */}
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold text-white">{pageTitle}</h1>
        </div>
        {/* Items prioritaires */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
          <h2 className="text-lg font-semibold text-white mb-4">{topSectionTitle}</h2>
          <div className="space-y-3">
            {topItems.map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-white/[0.08] flex items-center gap-4 p-4 bg-white/5 border-l-4 rounded-xl cursor-pointer hover:bg-white/10 transition"
                style={{ borderLeftColor: item.color }}
                onClick={() => setSelectedItemModal(item)}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                  style={{ backgroundColor: item.color }}
                >
                  #{index + 1}
                </div>
                <div className="text-2xl">{item.emoji}</div>
                <div className="flex-1">
                  <div className="font-semibold text-white">{item.name}</div>
                  <div className="text-xs text-zinc-400">{item.subtitle}</div>
                </div>
                <div className="text-zinc-500">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4M12 8h.01"/>
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Graphique complet */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
          <h2 className="text-lg font-semibold text-white mb-2">{allSectionTitle}</h2>
          <p className="text-xs text-zinc-500 mb-4">{allSectionSubtitle}</p>
          <table className="w-full border-collapse">
            <tbody className="[&>tr]:cursor-pointer">
              {processedItems.map((item, index) => {
                const isTopItem = item.rank <= topCount;

                return (
                  <motion.tr
                    key={item.key}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedItemModal(item)}
                    className="align-middle"
                  >
                    {/* Emoji */}
                    <td className="w-2 pr-2 text-center align-middle pb-2">
                      {item.emoji}
                    </td>

                    {/* Nom (wrap autorisé) */}
                    <td className="w-2 pr-4 align-middle pb-2">
                      <div className="text-sm text-zinc-300 whitespace-nowrap">
                        {item.name}
                      </div>
                    </td>

                    {/* Barre (prend le reste) */}
                    <td className="align-middle pb-2">
                      <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(8, Math.min(100, item.normalizedScore))}%` }}
                          transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
                          style={{
                            backgroundColor: item.color,
                            opacity: isTopItem ? 1 : 0.3
                          }}
                        />
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Synthèse IA */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
          <h2 className="text-lg font-semibold text-white mb-4">{synthesisSectionTitle}</h2>

          <div>
            {isLoadingSynthesis ? (
              <div className="flex flex-col items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500 mb-3"></div>
                <p className="text-zinc-400 text-sm">Analyse en cours...</p>
              </div>
            ) : synthesisError ? (
              <div className="text-center py-6">
                <p className="text-zinc-400 mb-2">La synthèse n'a pas pu être générée.</p>
                <p className="text-zinc-500 text-sm mb-4">Vérifie ta connexion internet et réessaie.</p>
                <button
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition"
                  onClick={handleRetrySynthesis}
                >
                  Réessayer la génération
                </button>
              </div>
            ) : (
              <>
                <div
                  className="synthesis-text"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      if (!synthesis) return '';

                      let html = synthesis
                        // **Titre** → <h4><strong>Titre</strong></h4>
                        .replace(/\*\*([^*]+)\*\*\s*/g, '\n<h4>$1</h4>\n')
                        .replace(/\n{3,}/g, '\n\n')
                        .trim();

                      const blocks = html.split(/\n\n+/);

                      return blocks
                        .map(block => {
                          block = block.trim();
                          if (!block) return '';

                          // 🟢 TITRE
                          if (block.startsWith('<h4>')) {
                            return block;
                          }

                          // 🟢 LISTE (- ...)
                          if (block.split('\n').every(line => line.trim().startsWith('- '))) {
                            const items = block
                              .split('\n')
                              .map(line => `<li>${line.replace(/^-+\s*/, '')}</li>`)
                              .join('\n');

                            return `<ul>${items}</ul>`;
                          }

                          // 🟢 PARAGRAPHE
                          return `<p>${block.replace(/\n/g, ' ')}</p>`;
                        })
                        .join('\n');
                    })()
                  }}
                />

                <div className="synthesis-disclaimer">
                  {disclaimer}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Partage parents */}
        {!isLoadingSynthesis && sendAgreement && (
          <div className="text-center w-full">
            <ParentShareHybrid onSendAgreement={sendAgreement} agreed={initialResults.share_to_parent} />
          </div>
        )}

        {/* Bouton recommencer */}
        {/* {!isLoadingSynthesis && onRestart && (
          <div className="mb-5 text-center">
            <button
              className="py-3 px-4 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-zinc-400 hover:text-zinc-300 text-sm rounded-xl transition"
              onClick={onRestart}
            >
              Recommencer le test
            </button>
          </div>
        )} */}

        <p className="text-center text-xs text-slate-600 leading-relaxed whitespace-pre-line">
          {attribution}
        </p>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedItemModal && (
          <ItemModal
            item={selectedItemModal}
            onClose={() => setSelectedItemModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
